import type { PersonnelVessel } from '../personnel/types.js';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyageReport } from './types.js';
import { assignedVoyageIdOf } from './voyage-record.js';
import { contextVoyageOf } from './voyage-start.js';

export interface VoyageContext {
  vessels: PersonnelVessel[];
  vessel: PersonnelVessel | null;
  voyage: Voyage | null;
  reports: VoyageReport[];
  /** Geminin bütün seferleri (sefer kararı listesi; ADR 0058 §3). */
  voyages: Voyage[];
  /**
   * Geminin sefersiz raporları (ADR 0083 §1; eski kaynakta sefersiz kalkışlar, ADR 0058 §3);
   * kaynak vermiyorsa boş.
   */
  unlinked: VoyageReport[];
  /** Sefersiz raporlar görünümü (`?voyage=none`): `reports` sefersiz raporlardır. */
  unlinkedView: boolean;
  /**
   * Geminin zaman çizgisi (ADR 0083 §1): sefere bakılmadan bütün raporları. Sıra, Next Operation
   * ve "son akış raporu" kuralı bunu kullanır. Eski kaynakta seferin raporları ve sefersizler.
   */
  timeline: VoyageReport[];
  /**
   * Son akış raporunun bağlamı (gemide; ADR 0157 §3): sefer kimliği, sefersizse `null`; kural
   * yoksa (ofis, eski kaynak, rapor yok) `undefined`. Açık `?voyage=` bununla çelişirse ekran
   * engellemeyen uyarı gösterir.
   */
  continuity?: string | null | undefined;
}

/** Sefersiz raporlar görünümünün bağlantı değeri (`?voyage=none`). */
export const UNLINKED = 'none';

/** Geminin bütün raporları (All Reports kaynağı); yoksa ya da hata verirse `null`. */
async function vesselTimeline(source: VoyageSource, vesselId: string) {
  if (!source.allReports) return null;
  try {
    return await source.allReports(vesselId);
  } catch {
    return null;
  }
}

/**
 * Sefersiz raporlar: yeni kaynakta bütün türler; eski sunucuda (uç yok) ya da kaynakta sefersiz
 * kalkışlar (ADR 0058 §3).
 */
async function unassignedOf(source: VoyageSource, vesselId: string): Promise<VoyageReport[]> {
  if (source.unassigned) {
    try {
      return await source.unassigned(vesselId);
    } catch (error) {
      if (!source.departures) throw error;
    }
  }
  return source.departures ? source.departures(vesselId) : [];
}

/**
 * Ekrandaki sefer: bağlantıda verilen (`?voyage=`), yoksa sürmekte olan. Gemide süren sefer yoksa
 * planlanan sefer açılır: ilk raporu (Departure ya da seferin ortasından ilk rapor) gemi girer
 * (ortağın notu #1, #2; ADR 0047 §1). Gemide tamamlanan ve iptal edilen sefere rapor açılmaz;
 * ofiste süren yoksa son tamamlanan (salt okunur). Belirli bir rapor açılırken (All Reports
 * "View"; `explicit`) bağlantıdaki sefer her durumda gösterilir: rapor ekranda yine salt okunurdur,
 * yeni rapor yalnız süren ya da planlanan sefere açılır (ADR 0082).
 */
export function shownVoyage(
  voyages: readonly Voyage[],
  editing: boolean,
  voyageId: string | null = null,
  explicit = false,
): Voyage | null {
  const writable = (v: Voyage) => v.status === 'active' || v.status === 'planned';
  const linked = voyages.find((v) => v.id === voyageId);
  if (linked && explicit) return linked;
  if (linked && (editing ? writable(linked) : linked.status !== 'cancelled')) return linked;
  return (
    voyages.find((v) => v.status === 'active') ??
    (editing
      ? (voyages.find((v) => v.status === 'planned') ?? null)
      : (voyages.find((v) => v.status === 'completed') ?? null))
  );
}

/**
 * Noon Report ve Leg / Event'in verisi tek okumada: gemiler, seçili gemi, seferi ve raporları.
 * Gemiler de her seferinde okunur: gemiler isteği hata verdiyse "Try again" boşa düşmez (G4-61).
 */
export async function loadVoyageContext(
  source: VoyageSource,
  {
    vesselId,
    editing,
    voyageId = null,
    reportId = null,
    unlinked: withUnlinked = false,
  }: {
    vesselId: string | null;
    editing: boolean;
    voyageId?: string | null;
    /** Açılacak rapor (`?report=`; All Reports): seferi durumundan bağımsız gösterilir. */
    reportId?: string | null;
    /** Sefersiz raporlar da okunur (Leg / Event; ADR 0058 §3, ADR 0083 §1). */
    unlinked?: boolean;
  },
): Promise<VoyageContext> {
  const vessels = await source.vessels();
  const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0] ?? null;
  const none = { voyages: [], unlinked: [], unlinkedView: false, timeline: [] };
  if (!vessel) return { vessels, vessel, voyage: null, reports: [], ...none };
  const [voyages, early, all] = await Promise.all([
    source.voyages(vessel.id),
    withUnlinked || voyageId === UNLINKED ? unassignedOf(source, vessel.id) : Promise.resolve(null),
    vesselTimeline(source, vessel.id),
  ]);
  // Bağlam sürekliliği (gemide; ADR 0157 §3): bağlantı yoksa son akış raporunun bağlamı
  const continuity = editing && all ? contextVoyageOf(all, voyages) : undefined;
  // `?report=` tek başına: raporun kendi bağlamı (sefersizse sefersiz görünüm). Sonraki Rapor
  // kartındaki taslak bağlantısı planlanan sefere düşmez (#2278)
  const opened =
    voyageId === null && reportId !== null ? all?.find((r) => r.id === reportId) : undefined;
  const followed =
    voyageId !== null
      ? undefined
      : reportId === null
        ? continuity
        : opened
          ? assignedVoyageIdOf(opened, voyages)
          : undefined;
  const wanted = followed === null ? UNLINKED : (followed ?? voyageId);
  // Sefersiz raporlar görünümü bağlantıyla, son raporla ya da (Leg / Event) seferi yokken açılır
  const wantsUnlinked = withUnlinked || wanted === UNLINKED;
  const unlinked = early ?? (wantsUnlinked ? await unassignedOf(source, vessel.id) : []);
  // Sefersiz raporlar: bağlantıyla ya da gösterilecek sefer yoksa (ADR 0058 §3)
  const voyage =
    wanted === UNLINKED ? null : shownVoyage(voyages, editing, wanted, reportId !== null);
  if (!voyage) {
    const timeline = all ?? unlinked;
    return wantsUnlinked
      ? {
          vessels,
          vessel,
          voyage: null,
          reports: unlinked,
          voyages,
          unlinked,
          unlinkedView: true,
          timeline,
          continuity,
        }
      : {
          vessels,
          vessel,
          voyage: null,
          reports: [],
          voyages,
          unlinked,
          unlinkedView: false,
          timeline,
          continuity,
        };
  }
  const reports = await source.reports(voyage.id);
  // Eski kaynak: seferin raporları ve sefersizler (kimliğe göre tekil)
  const timeline = all ?? [...new Map([...reports, ...unlinked].map((r) => [r.id, r])).values()];
  return {
    vessels,
    vessel,
    voyage,
    reports,
    voyages,
    unlinked,
    unlinkedView: false,
    timeline,
    continuity,
  };
}

/**
 * Açık bağlantı son raporun bağlamıyla çelişiyor mu (ADR 0157 §3, #2265): gösterilen bağlam
 * (sefer ya da sefersiz) son akış raporununkinden farklıysa `true`. Kural yoksa `false`.
 */
export function contextMismatch(context: Pick<VoyageContext, 'voyage' | 'continuity'>): boolean {
  if (context.continuity === undefined) return false;
  return (context.voyage?.id ?? null) !== context.continuity;
}

/**
 * Raporları sefere bağlar ve geminin zaman çizgisini yeniden okur (Overview "Önce bağla";
 * #2278): ekrandaki eski liste "Sefer başlatılmadı" uyarısını bağdan sonra da göstermesin.
 * Bağ yazılamazsa hata fırlar; çizgi okunamazsa `null`.
 */
export async function linkThenTimeline(
  source: VoyageSource,
  voyageId: string,
  vesselId: string,
  reportIds: readonly string[],
): Promise<VoyageReport[] | null> {
  if (!source.planner?.link) return null;
  await source.planner.link(voyageId, [...reportIds]);
  return vesselTimeline(source, vesselId);
}
